// biome-ignore format: historic
import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';
import { useCallback } from 'react';

interface useUnfocusedListControlsOptions<T extends string> {
  show: boolean;
  values: T[];
  highlightedValue: T | undefined;
  listNode: HTMLUListElement | undefined;
  onChangeHighlight: (value: T) => void;
  onSelect: (value: T) => void;
  containerNode?: HTMLElement;
}

/**
 * This hook makes the assumption that the values that you pass each correspond to a list item
 * element contained as direct children of the listNode. It also assumes that they are rendered
 * in the exact same order. This decision means that you don't need to query the DOM to calculate
 * which item we should highlight next and what item we need to scroll to if any. The tradeoff is
 * that these assumptions aren't easy to enforce. In the future we can have this hook return a DOM
 * property to attach to each node, so that it can ensure that there is no mismatch between the data
 * and what it expects to see on the DOM.
 * @param options Needed for interacting with the list and reporting back changes
 */
export default function useUnfocusedListControls<T extends string>({
  show,
  values,
  highlightedValue,
  listNode,
  onChangeHighlight,
  onSelect,
  containerNode,
}: useUnfocusedListControlsOptions<T>): React.KeyboardEventHandler<HTMLElement> {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!show) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!listNode) return;

          // Don't scroll the page
          event.preventDefault();
          const indexMove = event.key === 'ArrowDown' ? 1 : -1;

          const currentOptionIndex = highlightedValue ? values.indexOf(highlightedValue) : 0;
          const nextIndex = currentOptionIndex + indexMove;

          let boundedNextIndex = nextIndex;
          if (nextIndex < 0) boundedNextIndex = values.length - 1;
          if (nextIndex > values.length - 1) boundedNextIndex = 0;

          const nextOptionValue = values[boundedNextIndex];

          onChangeHighlight(nextOptionValue);

          break;
        }

        case 'Enter': {
          /* Need this! I had a wierd bug were pressing enter would open up a dialog and because
                        the close button was immediately focused, would also trigger the close button. */
          event.preventDefault();

          if (highlightedValue) {
            onSelect(highlightedValue);
          }

          break;
        }
      }
    },
    [listNode, show, highlightedValue, onChangeHighlight, onSelect, values],
  );

  // Because we aren't actually focusing any of the items the scroll position won't change if we
  // highlight an option not in view. So we need to fill this in ourselves with a little logic.
  useIsomorphicLayoutEffect(() => {
    if (!highlightedValue || !listNode) return;
    const container = containerNode ?? listNode;
    const currentOptionIndex = values.indexOf(highlightedValue);
    const itemsEl = listNode.children;
    const optionEl = itemsEl.item(currentOptionIndex);

    if (!optionEl || !(optionEl instanceof HTMLElement)) return;

    const distanceOutOfView = Math.max(
      0,
      optionEl.offsetTop - container.clientHeight + optionEl.clientHeight + 8,
    );

    container.scrollTo(0, distanceOutOfView);
  }, [highlightedValue, containerNode]);

  return handleKeyDown;
}
